import {
  BasicTextStyleButton,
  BlockNoteViewRaw,
  FormattingToolbar as BnFormattingToolbar,
  ColorStyleButton,
  ComponentsContext,
  CreateLinkButton,
  FormattingToolbarController,
  NestBlockButton,
  SideMenuController,
  SuggestionMenuController,
  TextAlignButton,
  UnnestBlockButton,
  useCreateBlockNote,
} from "@blocknote/react";
import styles from "./BlocksEditorV2.module.css";

import { filterSuggestionItems } from "@blocknote/core";
import { BlocksContext } from "blocks-schema";
import { type BnBlock, Schema } from "blocks-schema";
import { useNavigate } from "react-router";
import type { PersistedTheme } from "schemas";
import { EditPopover } from "../EditPopover";
import { Form } from "./Form/Form";
import { FormattingToolbar } from "./FormattingToolbar/FormattingToolbar";
import { GridSuggestionMenu } from "./GridSuggestionMenu/GridSuggestionMenu";
import { LinkToolbar } from "./LinkToolbar/LinkToolbar";
import { Menu } from "./Menu/Menu";
import { NewImage } from "./NewImage";
import { Popover } from "./Popover/Popover";
import { SetFontStyleButton } from "./SetFontStyleButton";
import { SideMenu } from "./SideMenu/SideMenu";
import { SuggestionMenu } from "./SuggestionMenu/SuggestionMenu";
import { FormLinkBlockDrawer } from "./editors/FormLinkBlockDrawer";
import { PageBlockDrawer } from "./editors/PageBlockDrawer";
import { SelectAppointmentDrawer } from "./editors/SelectAppointmentDrawer";
import { SocialLinksEditor } from "./editors/SocialLinksEditor";
import { getCustomSlashMenuItems } from "./slashMenuItems/getCustomSlashMenuItems";

interface Props {
  onChange: (blocks: BnBlock[]) => Promise<void>;
  parent: "Campaign" | "Page";
  theme: PersistedTheme;
  blocks: BnBlock[];
}
export function BlocksEditorV2({ blocks, theme, parent, onChange }: Props) {
  const navigate = useNavigate();
  const editor = useCreateBlockNote({
    schema: Schema,
    defaultStyles: false,
    initialContent: blocks.length > 0 ? blocks : undefined,
  });

  const onPageEdit = (pageId: string) => {
    navigate(`/pages/${pageId}`);
  };

  const onBlockChange = async () => {
    await onChange(editor.document);
  };
  return (
    <div className={styles.wrapper}>
      <BlocksContext.Provider
        value={{
          SelectAppointmentDrawer,
          FormLinkBlockDrawer,
          PageBlockDrawer,
          onPageEdit,
          SocialLinksEditor,
          NewImage,
          EditPopover,
        }}
      >
        <ComponentsContext.Provider
          value={{
            FormattingToolbar,
            FilePanel: {
              Root: () => <div>Panel</div>,
              Button: () => <button type="button">Panel Button</button>,
              FileInput: () => <input type="file" />,
              TabPanel: () => <div>Panel Tab</div>,
              TextInput: () => <input type="text" />,
            },
            LinkToolbar,
            SideMenu,
            SuggestionMenu,
            GridSuggestionMenu,
            TableHandle: {
              Root: () => <div>Table Handle</div>,
              ExtendButton: () => <button type="button">Extend</button>,
            },
            Comments: {
              Comment: () => <div>Comment</div>,
              Editor: () => <div>Comment Editor</div>,
              Card: () => <div>Comment Card</div>,
              CardSection: () => <div>Card Section</div>,
              ExpandSectionsPrompt: () => <div>Expand Sections</div>,
            },
            Generic: {
              Badge: {
                Root: () => <div>Badge</div>,
                Group: () => <div>Badge Group</div>,
              },
              Toolbar: {
                Root: () => <div>Generic Toolbar</div>,
                Button: () => <button type="button">Toolbar Button</button>,
                Select: () => (
                  <select>
                    <option>Toolbar Select</option>
                  </select>
                ),
              },
              Menu,
              Form,
              Popover,
            },
          }}
        >
          <BlockNoteViewRaw
            editor={editor}
            formattingToolbar={false}
            sideMenu={false}
            slashMenu={false}
            onChange={onBlockChange}
          >
            <SuggestionMenuController
              triggerCharacter={"/"}
              getItems={async (query) =>
                filterSuggestionItems(getCustomSlashMenuItems({ parent, editor, theme }), query)
              }
            />
            <SideMenuController floatingOptions={{ placement: "left" }} />
            <FormattingToolbarController
              formattingToolbar={() => (
                <BnFormattingToolbar>
                  <BasicTextStyleButton basicTextStyle={"bold"} key={"boldStyleButton"} />
                  <BasicTextStyleButton basicTextStyle={"italic"} key={"italicStyleButton"} />
                  <BasicTextStyleButton basicTextStyle={"underline"} key={"underlineStyleButton"} />
                  <BasicTextStyleButton basicTextStyle={"strike"} key={"strikeStyleButton"} />
                  <SetFontStyleButton />

                  <TextAlignButton textAlignment={"left"} key={"textAlignLeftButton"} />
                  <TextAlignButton textAlignment={"center"} key={"textAlignCenterButton"} />
                  <TextAlignButton textAlignment={"right"} key={"textAlignRightButton"} />

                  <ColorStyleButton key={"colorStyleButton"} />

                  <NestBlockButton key={"nestBlockButton"} />
                  <UnnestBlockButton key={"unnestBlockButton"} />

                  <CreateLinkButton key={"createLinkButton"} />
                </BnFormattingToolbar>
              )}
            />
          </BlockNoteViewRaw>
        </ComponentsContext.Provider>
      </BlocksContext.Provider>
    </div>
  );
}
